<script setup>
import { ref, computed } from 'vue';
import { VueUiStackbar } from 'vue-data-ui/vue-ui-stackbar';
import { VueUiStackline } from 'vue-data-ui/vue-ui-stackline';
import { VueUiXy } from 'vue-data-ui/vue-ui-xy';

import { store } from '~/store';

import { xyConfig, stackedBarConfig, stackedLineConfig, stackedDataset, xyDataset } from './chartConfig';
import 'vue-data-ui/style.css';

const props = defineProps({
  graphPoints: {
    type: Array,
    required: true,
  },
  graphDates: {
    type: Array,
    required: true,
  },
  chartType: {
    type: String,
    default: 'bar',
  },
  stacked: {
    type: Boolean,
    default: true,
  },
  allSeries: {
    type: Array,
    required: true,
  },
  seriesLabels: {
    type: Object,
    default: () => ({}),
  },
  stackedBarOptions: {
    type: Object,
    default: () => ({}),
  },
});

const chartRef = ref(null);

const emit = defineEmits(['update:activeSeries', 'selectLegend']);

const chart = computed(() => {
  if (!props.stacked) {
    return {
      component: VueUiXy,
      dataset: xyDataset(props.allSeries, props.chartType, props.graphPoints, props.seriesLabels),
      config: xyConfig(props.graphDates, store.theme),
    };
  }

  if (props.chartType === 'line') {
    return {
      component: VueUiStackline,
      dataset: stackedDataset(props.allSeries, props.graphPoints, props.seriesLabels),
      config: stackedLineConfig(props.graphDates, store.theme),
    };
  }

  return {
    component: VueUiStackbar,
    dataset: stackedDataset(props.allSeries, props.graphPoints, props.seriesLabels),
    config: stackedBarConfig(props.graphDates, store.theme, props.stackedBarOptions),
  };
});

const handleSelectLegend = (eventData) => {
  emit('update:activeSeries', eventData.map(series => series.name));
};

const hideSeries = series => chartRef.value?.hideSeries(series);
defineExpose({ hideSeries });
</script>

<template>
  <Component
    :is="chart.component"
    ref="chartRef"
    :config="chart.config"
    :dataset="chart.dataset"
    @select-legend="handleSelectLegend"
  />
</template>
